import { useRef } from "react";
import type { useInstantSearch } from "react-instantsearch";
import { getSearchSession, type SearchSessionKey } from "@/lib/search-session-store";

type SearchStatus = ReturnType<typeof useInstantSearch>["status"];

interface StableSearchInput<THit> {
  hasMore: boolean;
  hits: THit[];
  status: SearchStatus;
  totalCount: number;
  /** Persist the settled snapshot in the session store so a revisit paints it
   *  instantly (stale-while-revalidate) instead of a skeleton. */
  sessionKey?: SearchSessionKey;
}

interface StableSearchOutput<THit> {
  hasMore: boolean;
  hits: THit[];
  isInitialLoading: boolean;
  totalCount: number;
}

type Snapshot<THit> = { hasMore: boolean; hits: THit[]; totalCount: number };

// InstantSearch is "idle" both before the first search and after one settles,
// so we track `hasSearched` to distinguish pre-search idle (skeleton) from a
// post-search idle (snapshot). On "error" we also end loading so consumers
// can render an error UI instead of an indefinite skeleton.
export function useStableSearchResults<THit>({
  hasMore,
  hits,
  status,
  totalCount,
  sessionKey,
}: StableSearchInput<THit>): StableSearchOutput<THit> {
  const session = sessionKey ? getSearchSession(sessionKey) : undefined;
  const hasSearched = useRef(false);
  const snapshot = useRef<Snapshot<THit> | null>(null);

  // Seed from the session's last settled results so a revisit renders them
  // immediately while the fresh search runs in the background.
  if (!hasSearched.current && !snapshot.current && session?.resultsSnapshot) {
    snapshot.current = session.resultsSnapshot as Snapshot<THit>;
  }

  if (status === "loading" || status === "stalled") {
    hasSearched.current = true;
  }

  if (status === "idle" && hasSearched.current) {
    snapshot.current = { hasMore, hits, totalCount };
    if (session) session.resultsSnapshot = snapshot.current;
  }

  const stable = snapshot.current;
  const isInitialLoading = !stable && status !== "error";

  return {
    hasMore: stable ? stable.hasMore : hasMore,
    hits: stable ? stable.hits : hits,
    isInitialLoading,
    totalCount: stable ? stable.totalCount : totalCount,
  };
}
